import { zip } from "lodash";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";

export type CloneIdsGroupedBySourceIds = Record<
  uuid,
  { clonedQuestionId: uuid; type: string; optionIdMapping?: Record<uuid, uuid> }
>;

/**
 * AnswerBank records only know about its CustomQuestionType and the respective
 * (CustomQuestionType)Fields.
 *
 * The nestedQuestions of a CustomQuestion know its source via its customQuestionTypeFieldId.
 * That property is used to construct this mapping from source to clone.
 *
 * When setting the answer to a CustomQuestion in Formik, the questionIds and optionIds are based
 * on the AnswerBank (source). However these ids must be converted to that of the cloned records (target).
 */
export function mapCloneIdsBySourceIds(
  customQuestionType: GQL.GetCustomQuestionType_custom_question_type_by_pk,
  nestedQuestions: AF.ClonedQuestion<AF.WithId>[]
): CloneIdsGroupedBySourceIds {
  return customQuestionType.custom_question_type_fields.reduce<CloneIdsGroupedBySourceIds>(
    (accumulator, currentField, index) => {
      const { question_id: fieldId } = currentField;
      const clonedQuestion = nestedQuestions[index];

      if (
        !(
          clonedQuestion && clonedQuestion.customQuestionTypeFieldId === fieldId
        ) ||
        currentField.question.type !== clonedQuestion.type
      ) {
        throw new Error(
          `Could not find cloned question corresponding to customQuestionTypeFieldId ${fieldId} `
        );
      }

      switch (clonedQuestion.type) {
        case AF.FreeTextType:
        case AF.EmailType:
        case AF.PhoneNumberType:
        case AF.DateType:
        case AF.NumberType:
          // TODO: handle date and number type
          accumulator[fieldId] = {
            clonedQuestionId: clonedQuestion.id,
            type: clonedQuestion.type,
          };
          break;
        case AF.SingleSelectType:
          verifyFormOptions(
            currentField.question.form_question?.form_question_options,
            clonedQuestion.options
          );

          const sourceOptionIds =
            currentField.question.form_question?.form_question_options.map(
              (option) => {
                return option.id;
              }
            );

          const targetOptionIds = clonedQuestion.options.map((option) => {
            return option.id;
          });

          accumulator[fieldId] = {
            clonedQuestionId: clonedQuestion.id,
            type: clonedQuestion.type,
            optionIdMapping: Object.fromEntries(
              zip(sourceOptionIds, targetOptionIds)
            ),
          };
          break;
        default: {
          const _exhaustiveCheck: never = clonedQuestion;
          return _exhaustiveCheck;
        }
      }

      return accumulator;
    },
    {}
  );
}

/**
 * These options are both provided in sorted order so it's assumed they're matching:
 * - sourceFormOptions is sorted via GQL query
 * - clonedFormOptions is sorted client side
 */
function verifyFormOptions(
  sourceFormOptions:
    | GQL.GetCustomQuestionType_custom_question_type_by_pk_custom_question_type_fields_question_form_question_form_question_options[]
    | undefined,
  clonedFormOptions: AF.Option<AF.WithId>[]
): void {
  if (!sourceFormOptions) {
    throw new Error("Source form options is undefined.");
  }

  if (sourceFormOptions.length !== clonedFormOptions.length) {
    throw new Error("Form option lengths do not match.");
  }

  const fieldsToCompare = ["label", "value"];

  function areFieldsEqual(a: any, b: any, field: string): boolean {
    // Treat null and undefined as equal
    const aValue = a[field] ?? null;
    const bValue = b[field] ?? null;

    return aValue === bValue;
  }

  const optionsAreEqual = sourceFormOptions.every((sourceOption, idx) => {
    const clonedOption = clonedFormOptions[idx];
    return fieldsToCompare.every((field) =>
      areFieldsEqual(sourceOption, clonedOption, field)
    );
  });

  if (!optionsAreEqual) {
    throw new Error("Source form options do not match cloned form options.");
  }
}
